import { useElementSize } from '@mantine/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { getIconButtonSx } from 'components/Search/styles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { memo, useEffect, useRef, useState } from 'react';
import { isDeepEqual } from 'remeda';
import { safeMaxDateTime, safeMinDateTime, UTC_MM_DD_YY_HHMM } from 'utility/constants';
import { formatDate } from 'utility/functions';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import { v4 as uuidv4 } from 'uuid';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  getCloseButtonSx,
  getDropdownInputThreshold,
  getFooterContainerSx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  maxPopoverHeightVh,
  maxPopoverWidthPxDateRange,
  tooltipPopperProps,
} from './dropdownConsts';
import { PossibleEvent } from './SelectDropdown/SelectDropdownInput';
import { SelectRenderValueElem } from './SelectDropdown/SelectRenderValueElem';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(timezone);
dayjs.extend(utc);

type ZuluTimeString = string;

type DateTimeDropdownInputProps = {
  inputLabel?: string;
  value?: string;
  updateValue: (event: PossibleEvent | any, value: string) => void;
  formControlSx?: SxProps<Theme>;
  popoverPaperSx?: SxProps<Theme>;
  formControlVariant?: 'standard' | 'outlined';
  renderValueTooltip?: boolean;
  renderValueDisplay?: 'label' | 'chips';
  popoverMatchesFormControlWidth?: boolean;
  applyChangesOnPopoverClose?: boolean;
  inputPlaceholderText?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** If the time is empty when a valid date is captured, should the time be set to the start of the day? */
  defaultTimeToStart?: boolean;
  infoText?: string;
  timePicker?: boolean;
};

export const baseDateFmtOpts: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'UTC',
};

export function checkValidDate(date: string) {
  return date === '' || dayjs(date).isValid();
}

export function getDateFormatOptions(includeTime: boolean): Intl.DateTimeFormatOptions {
  if (includeTime) {
    return baseDateFmtOpts;
  }

  const { timeStyle, ...dateOnlyOpts } = baseDateFmtOpts;
  return dateOnlyOpts;
}

export function getFormattedDateLabel(dateValue: string, dateTime = true) {
  const hasValue = dateValue !== '';
  const isValid = checkValidDate(dateValue);

  if (!hasValue) {
    return '';
  }

  if (!isValid) {
    return 'Invalid date.';
  }

  return formatDate({ date: dateValue, format: UTC_MM_DD_YY_HHMM });
}

const getDatePart = (dateTime: string) => {
  const date = dayjs(dateTime).utc();
  const result = date.isValid() ? date.format('YYYY-MM-DD') : '';
  return result;
};

const getTimePart = (dateTime: string, fallbackToStart = false) => {
  const date = dayjs(dateTime).utc();
  const invalidFallback = fallbackToStart ? '00:00' : '00:00';
  const result = date.isValid() ? date.format('HH:mm') : invalidFallback;
  return result;
};

const combineDateTime = (date: string, time?: string): ZuluTimeString => {
  if (!date) return '';
  return time ? dayjs(`${date}T${time}Z`).utc().format() : dayjs(date).utc().format();
};

export const DateTimeDropdownInput = memo(
  ({
    inputLabel = 'Select Date/Time',
    value = '',
    inputPlaceholderText = inputLabel,
    updateValue,
    formControlSx,
    popoverPaperSx,
    formControlVariant = 'standard',
    renderValueTooltip,
    renderValueDisplay = 'label',
    infoText = '',
    popoverMatchesFormControlWidth = false,
    applyChangesOnPopoverClose = false,
    disabled = false,
    hidden = false,
    defaultTimeToStart = false,
    timePicker = true,
  }: DateTimeDropdownInputProps) => {
    // UI Vars / Hooks
    const theme = useTheme();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();

    // Clear button workaround for MUI Date/Time internal state
    // they don't expose a method to clear the value so we just have to remount it
    const [pickerKeys, setPickerKeys] = useState({
      dateKey: uuidv4(),
      timeKey: uuidv4(),
    });

    // Setup consts / state for applyChangesOnPopoverClose prop
    const [stagedValue, setStagedValue] = useState<string>('');

    // Sync values passed in to internal state...
    useEffect(() => {
      // Don't update local state if there's nothing to update
      if (isDeepEqual(value, stagedValue)) {
        return;
      }
      // Don't update local state if the popover is open
      // and we're in applyChangesOnPopoverClose mode
      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      setStagedValue(value); // If we pass the guards, update the local state
    }, [stagedValue, popoverOpen, applyChangesOnPopoverClose, value]);

    // Determine the current value to use for the select - via passed value or local state copy?
    const currentValue = applyChangesOnPopoverClose ? stagedValue : value;

    // Const helpers
    const isDisabled = disabled;
    const isHidden = hidden;
    const hasValue = Boolean(currentValue);
    const isValid = checkValidDate(currentValue);
    const isError = hasValue && !isValid;

    const valueOrEmptyStr = currentValue || '';

    const formattedDateLabel = getFormattedDateLabel(currentValue, timePicker);

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip ? formattedDateLabel : '';
    const tooltipTitle = !popoverOpen ? tooltipText : '';

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      let adjustedValue = stagedValue;

      // Clear only if invalid
      if (isError) {
        adjustedValue = '';
      }

      updateValue(null, adjustedValue);
      setPopoverOpen(false);
    };

    const handleDateTimeChange = (
      newValue: any,
      isDate: boolean,
      timePicker: boolean,
    ) => {
      const formatted = newValue
        ? dayjs(newValue)
            .utc()
            .format(isDate ? 'YYYY-MM-DD' : 'HH:mm')
        : '';

      let newDateTime: string;

      if (timePicker) {
        newDateTime = combineDateTime(
          isDate ? formatted : getDatePart(currentValue),
          isDate ? getTimePart(currentValue, defaultTimeToStart) : formatted,
        );
      } else {
        newDateTime = isDate ? formatted : currentValue;
      }

      if (applyChangesOnPopoverClose) {
        setStagedValue(newDateTime);
      } else {
        updateValue(null, newDateTime);
      }
    };

    const handleDateChange = (newDate: any) =>
      handleDateTimeChange(newDate, true, timePicker);

    const handleTimeChange = (newTime: any) =>
      handleDateTimeChange(newTime, false, timePicker);

    const handleClear = (event: PossibleEvent) => {
      setPickerKeys({
        dateKey: uuidv4(),
        timeKey: uuidv4(),
      }); // Remount all inputs on clear

      if (applyChangesOnPopoverClose) {
        setStagedValue('');
      } else {
        updateValue(event, '');
      }
    };

    return (
      <Tooltip
        title={tooltipTitle}
        disableInteractive={true}
        disableFocusListener={true}
        slotProps={{
          tooltip: {
            sx: {
              ...getTooltipSx(theme),
            },
          },
        }}
      >
        <FormControl
          size="small"
          variant={formControlVariant}
          ref={elSizeRef}
          disabled={isDisabled}
          hidden={isHidden}
          sx={{
            ...getFormControlSx(theme, popoverOpen),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
            ...(isHidden ? { display: 'none' } : {}),
          }}
        >
          <Select
            ref={popoverAnchorRef}
            multiple={false}
            disabled={isDisabled}
            hidden={isHidden}
            id={`${inputLabel}-datetime-select-input`}
            value={valueOrEmptyStr}
            MenuProps={{ open: false }} // use custom popover
            onOpen={handlePopoverOpen}
            open={popoverOpen}
            sx={getSelectSx}
            displayEmpty={true}
            renderValue={() => (
              <SelectRenderValueElem
                selectedChoices={
                  valueOrEmptyStr
                    ? [{ value: valueOrEmptyStr, label: formattedDateLabel }]
                    : []
                }
                placeholderText={inputPlaceholderText || inputLabel}
                renderValueDisplay={renderValueDisplay}
                isError={!isValid && hasValue}
              />
            )}
          >
            {/**
             * To suppress the out-of-range value warning: add the empty default & always have the current value as a choice.
             */}
            <MenuItem
              value=""
              hidden={true}
              style={{ display: 'none' }}
            />
            <MenuItem
              value={valueOrEmptyStr}
              hidden={true}
              style={{ display: 'none' }}
            />
          </Select>

          <Popover
            anchorEl={popoverAnchorRef.current}
            open={popoverOpen && !isDisabled}
            hidden={isHidden}
            onClose={handlePopoverClose}
            anchorOrigin={dropdownInputAnchorOrigin}
            transformOrigin={dropdownInputTransformOrigin}
            elevation={dropdownInputElevation}
            marginThreshold={getDropdownInputThreshold(theme)}
            slotProps={{
              paper: {
                sx: {
                  display: 'grid',
                  gridTemplateRows: 'auto auto 1fr',
                  gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                  container: 'selectgrid / normal',
                  overflow: 'hidden',
                  width: popoverMatchesFormControlWidth
                    ? formCtrlWidth
                    : maxPopoverWidthPxDateRange,
                  maxHeight: maxPopoverHeightVh,
                  gap: 0.5,
                  bgcolor: getSolidThemeGray(theme),
                  ...getCommonPopoutSx(theme),
                  ...(typeof popoverPaperSx === 'function'
                    ? popoverPaperSx(theme)
                    : popoverPaperSx),
                },
              },
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gridArea: 'selectheader',
                p: 1,
                borderBottom: `1px solid ${theme.palette.action.selected}`,
              }}
            >
              <Tooltip
                title={infoText}
                arrow={false}
                disableInteractive={true}
                enterDelay={500}
                placement="bottom-start"
                slotProps={{
                  popper: tooltipPopperProps,
                }}
              >
                <Typography
                  sx={getHeaderLabelTypoSx}
                  aria-label={`${inputLabel} - ${infoText}`}
                >
                  {inputLabel}
                  {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                </Typography>
              </Tooltip>
              <Button
                variant="text"
                size="small"
                color="error"
                disabled={!hasValue}
                startIcon={<RemoveCircleOutlineIcon />}
                sx={getRemoveButtonSx}
                onClick={handleClear}
              >
                Remove
              </Button>
            </Stack>

            <Stack
              direction="column"
              sx={{ gridArea: 'selectmainarea', px: 1 }}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <DatePicker
                  key={pickerKeys.dateKey}
                  label="Date"
                  value={dayjs(currentValue).utc()}
                  onChange={handleDateChange}
                  disabled={isDisabled}
                  timezone="UTC"
                  maxDate={safeMaxDateTime}
                  minDate={safeMinDateTime}
                  slotProps={{
                    actionBar: {
                      actions: ['cancel', 'accept'],
                    },
                    textField: {
                      error: isError,
                      variant: 'outlined',
                      size: 'small',
                      sx: {
                        width: '100%',
                        bgcolor: theme.palette.background.paper,
                      },
                    },
                  }}
                />
                {timePicker && (
                  <>
                    <TimePicker
                      key={pickerKeys.timeKey}
                      label="Time"
                      value={currentValue ? dayjs(currentValue).utc() : null}
                      timezone="UTC"
                      maxTime={safeMaxDateTime}
                      minTime={safeMinDateTime}
                      onChange={handleTimeChange}
                      disabled={
                        isDisabled || !currentValue || !dayjs(currentValue).isValid()
                      }
                      slotProps={{
                        actionBar: {
                          actions: ['cancel', 'accept'],
                        },
                        textField: {
                          error: isError,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            bgcolor: theme.palette.background.paper,
                          },
                        },
                      }}
                    />
                  </>
                )}
                <Tooltip title="Clear">
                  <IconButton
                    sx={getIconButtonSx}
                    onClick={handleClear}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gridTemplateAreas: `"listinfo closebtn"`,
                alignItems: 'center',
                ...getFooterContainerSx(theme, true),
              }}
            >
              {/* Spacer to keep the close button location consistent with other advanced filters */}
              <Box sx={{ gridArea: 'listinfo' }} />

              <ButtonBase
                onClick={handlePopoverClose}
                sx={{
                  gridArea: 'closebtn',
                  ...getCloseButtonSx(theme),
                }}
              >
                Close
              </ButtonBase>
            </Stack>
          </Popover>
        </FormControl>
      </Tooltip>
    );
  },
);

DateTimeDropdownInput.displayName = 'DateTimeDropdownInput';
